import { React, useState, useEffect, useRef } from 'react';

// mui
import {
    Box,
    Collapse,
    Divider,
    Skeleton,
    Stack,
    Button,
    MenuItem,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

// react-virtuoso
import { Virtuoso } from 'react-virtuoso';

// api
import {
    FilterSearchList,
    AdminUserSearch,
    AdminUserChangeBadge,
    AdminUserUnsetLegal,
    AdminUserChangeNsfw,
    AdminUserChangeStatus,
    AdminUserChangeType,
    AdminUserChangeSubscription,
    AdminUserDelete,
} from '../../../api';

import { def } from '@goldblack/shared';

// client-shared
import { storage } from '@goldblack/client-storage';
import { ContainerScroll, FilterDescHeader, Message } from '@goldblack/client-comps';

// components
import {
    UserListItem,
    LoiseUserInfoPanel,
    LoiseUserStatusDialog,
    LoiseUserTypeDialog,
    LoiseUserSubscriptionDialog,
    LoiseUserDeleteDialog,
    AdminMenuBar,
} from '../..';

function LoiseFilterItem({ filter, value, onChange, onSearch }) {
    return (
        <TextField
            variant='standard'
            size='small'
            fullWidth
            autoComplete='new-password'
            spellCheck={false}
            value={value ?? ''}
            onChange={(e) => onChange(filter.name, e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
            }}
            slotProps={{
                input: {
                    style: { fontSize: '14px' },
                    endAdornment: (value ?? '') && (
                        <InputAdornment position='end'>
                            <IconButton size='small' onClick={() => onChange(filter.name, '')}>
                                <ClearIcon sx={{ fontSize: '14px' }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
}

function LoiseFilterCollapse({ open, loading, filterData, filterObject, onFilterChange, onSearch }) {
    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Collapse
                in={open}
                timeout='auto'
                unmountOnExit={false}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    maxHeight: '100%',
                    overflow: 'hidden',
                    '& .MuiCollapse-wrapper': {
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    '& .MuiCollapse-wrapperInner': {
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}>
                <Box
                    sx={{
                        height: '100%',
                        minHeight: 0,
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    <ContainerScroll sx={{ height: '100%', maxHeight: '100%', flex: 1, minHeight: 0 }}>
                        {loading ? (
                            <Skeleton variant='rectangular' height={26 * 2} sx={{ m: 1 }} />
                        ) : (
                            <TableContainer sx={{ p: 1 }}>
                                <Table padding='checkbox'>
                                    <TableBody>
                                        {filterData.map((filter) => (
                                            <TableRow key={filter._id}>
                                                <TableCell
                                                    sx={{
                                                        width: '20%',
                                                        borderBottom: 'none',
                                                        color: 'primary.main',
                                                    }}>
                                                    {filter.name}
                                                </TableCell>
                                                <TableCell align='right' sx={{ borderBottom: 'none' }}>
                                                    <LoiseFilterItem
                                                        filter={filter}
                                                        value={filterObject[filter.name.toLowerCase()]}
                                                        onChange={onFilterChange}
                                                        onSearch={onSearch}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </ContainerScroll>
                </Box>
            </Collapse>
        </Box>
    );
}

function LoiseUsersSearch(props) {
    const { loise } = storage.transience.getValue();
    const [openFilter, setOpenFilter] = useState(false);
    const [filterObject, setFilterObject] = useState({});
    const [filterDesc, setFilterDesc] = useState({});
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [typeValue, setTypeValue] = useState(-1);
    const [statusValue, setStatusValue] = useState(-1);
    const [openType, setOpenType] = useState(false);
    const [openStatus, setOpenStatus] = useState(false);
    const [openDelete, setDelete] = useState(false);
    const [openSubscription, setOpenSubscription] = useState(false);
    const [data, setData] = useState({ loadmore: false, skip: 0, data: [] });
    const [pendingBadges, setPendingBadges] = useState(null);
    const [pendingRestriction, setPendingRestriction] = useState({});
    const [pendingLegal, setPendingLegal] = useState([]);
    const scrollParentRef = useRef(null);
    const virtuosoRef = useRef(null);
    const searchGenRef = useRef(0);

    const getSelectedUser = () => (data.data.length > 0 ? data.data[selectedIndex] : null);

    const makeFilterDesc = (fo) => {
        const desc = {};
        for (const [key, val] of Object.entries(fo)) {
            if (val) desc[key.charAt(0).toUpperCase() + key.slice(1)] = val;
        }
        return desc;
    };

    const makeGuardedSetData = (gen) => (updater) => {
        if (searchGenRef.current !== gen) return;
        setData(updater);
    };

    const doSearch = (fo) => {
        const gen = ++searchGenRef.current;
        setData({ loadmore: true, skip: 0, data: [] });
        AdminUserSearch('loise', fo, 0, props.config?.searchLimit ?? 20, makeGuardedSetData(gen));
    };

    const onFilterChange = (name, value) => {
        const next = { ...filterObject, [name.toLowerCase()]: value };
        setFilterObject(next);
        setFilterDesc(makeFilterDesc(next));
    };

    const onClickSearch = () => {
        doSearch(filterObject);
    };

    const loadMoreRows = () => {
        if (data.loadmore) {
            AdminUserSearch(
                'loise',
                filterObject,
                data.skip,
                props.config?.searchLimit ?? 20,
                makeGuardedSetData(searchGenRef.current),
            );
        }
    };

    const onClickAdmin = () => {
        if (!getSelectedUser()) return;
        setTypeValue(def.UserType.ADMIN);
        setOpenType(true);
    };
    const onClickUser = () => {
        if (!getSelectedUser()) return;
        setTypeValue(def.UserType.USER);
        setOpenType(true);
    };
    const onClickBlock = () => {
        if (getSelectedUser()) {
            setOpenStatus(true);
            setStatusValue(def.UserStatus.BLOCKED);
        }
    };
    const onClickActive = () => {
        if (getSelectedUser()) {
            setOpenStatus(true);
            setStatusValue(def.UserStatus.ACTIVE);
        }
    };
    const onClickInactive = () => {
        if (getSelectedUser()) {
            setOpenStatus(true);
            setStatusValue(def.UserStatus.INACTIVE);
        }
    };
    const onClickUpdate = () => {
        const u = getSelectedUser();
        if (!u) return;
        if (pendingBadges && Object.keys(pendingBadges).length > 0) {
            AdminUserChangeBadge(u._id, pendingBadges, (user) => {
                setPendingBadges(null);
                setData((prev) => ({
                    ...prev,
                    data: prev.data.map((d, i) => (i === selectedIndex ? { ...d, badges: user.badges } : d)),
                }));
            });
        } else {
            setData((prev) => ({
                ...prev,
                data: prev.data.map((d, i) => (i === selectedIndex ? { ...d } : d)),
            }));
        }
        if (pendingLegal.length > 0) {
            AdminUserUnsetLegal(u._id, pendingLegal, () => {
                setPendingLegal([]);
                // new object → info panel reloads the profile
                setData((prev) => ({
                    ...prev,
                    data: prev.data.map((d, i) => (i === selectedIndex ? { ...d } : d)),
                }));
            });
        }
        Object.entries(pendingRestriction).forEach(([type, value]) => {
            if (typeof value !== 'boolean') return;
            AdminUserChangeNsfw(u._id, type, value, (nsfw, t) => {
                setPendingRestriction((prev) => ({ ...prev, [t]: nsfw }));
            });
        });
    };
    // Discards pending edits and reloads the selected user's profile (a new object makes the info panel refetch).
    const onClickRefresh = () => {
        if (!getSelectedUser()) return;
        setPendingBadges(null);
        setPendingRestriction({});
        setPendingLegal([]);
        setData((prev) => ({
            ...prev,
            data: prev.data.map((d, i) => (i === selectedIndex ? { ...d } : d)),
        }));
    };
    const onClickDelete = () => {
        if (getSelectedUser()) setDelete(true);
    };
    const onClickSubscription = () => {
        if (getSelectedUser()) setOpenSubscription(true);
    };
    const onBadgeChange = (badge, value) => {
        if (!getSelectedUser()) return;
        setPendingBadges((prev) => ({ ...(prev ?? {}), [badge]: value }));
    };
    const onClickSetVerified = () => onBadgeChange('verified', true);
    const onClickUnsetVerified = () => onBadgeChange('verified', false);
    const onClickSetMembership = () => onBadgeChange('membership', true);
    const onClickUnsetMembership = () => onBadgeChange('membership', false);
    const onClickUnsetLegal = (type) => () => {
        if (!getSelectedUser()) return;
        setPendingLegal((prev) => (prev.includes(type) ? prev : [...prev, type]));
    };
    const onClickSetRestriction = (type) => () => {
        if (!getSelectedUser()) return;
        setPendingRestriction((prev) => ({ ...prev, [type]: true }));
    };
    const onClickUnsetRestriction = (type) => () => {
        if (!getSelectedUser()) return;
        setPendingRestriction((prev) => ({ ...prev, [type]: false }));
    };

    const onClickStatusOk = (expire, forced, reason) => {
        setOpenStatus(false);
        const user = getSelectedUser();
        if (!user) return;
        switch (statusValue) {
            case def.UserStatus.ACTIVE:
            case def.UserStatus.BLOCKED:
            case def.UserStatus.INACTIVE:
                AdminUserChangeStatus(user._id, statusValue, expire, forced, reason);
                break;
        }
    };
    const onClickTypeOk = () => {
        setOpenType(false);
        AdminUserChangeType(getSelectedUser()._id, typeValue);
    };
    const onClickDeleteOk = () => {
        setDelete(false);
        const userId = getSelectedUser()._id;
        AdminUserDelete(userId, 'loise', () => {
            setData((prev) => ({ ...prev, data: prev.data.filter((u) => u._id !== userId) }));
        });
    };
    const onClickSubscriptionOk = (status, plan, expires_at) => {
        setOpenSubscription(false);
        const userId = getSelectedUser()._id;
        AdminUserChangeSubscription(userId, status, plan, expires_at, (user) => {
            setData((prev) => ({
                ...prev,
                data: prev.data.map((u) => (u._id === userId ? user : u)),
            }));
        });
    };

    const onCloseType = (e, reason) => {
        if (reason !== 'backdropClick') setOpenType(false);
    };
    const onCloseStatus = (e, reason) => {
        if (reason !== 'backdropClick') setOpenStatus(false);
    };
    const onCloseDelete = (e, reason) => {
        if (reason !== 'backdropClick') setDelete(false);
    };
    const onCloseSubscription = (e, reason) => {
        if (reason !== 'backdropClick') setOpenSubscription(false);
    };

    useEffect(() => {
        setPendingBadges(null);
        setPendingRestriction({});
        setPendingLegal([]);
    }, [selectedIndex]);

    const deeplinkKeyRef = useRef(null);

    useEffect(() => {
        if (loise.filter.data.length === 0) {
            FilterSearchList('loise', def.FilterGroup.ADMIN);
        }
    }, []);

    useEffect(() => {
        const deeplink = props.deeplink;
        if (!deeplink || deeplink._key === deeplinkKeyRef.current) return;
        if (deeplink.path[0] !== 'loise' || deeplink.path[1] !== 'users' || deeplink.path[2] !== 'search') return;
        deeplinkKeyRef.current = deeplink._key;
        const { username } = deeplink.params;
        if (!username) return;
        const fo = { username };
        const gen = ++searchGenRef.current;
        setFilterObject(fo);
        setFilterDesc({ username });
        setSelectedIndex(0);
        setData({ loadmore: true, skip: 0, data: [] });
        AdminUserSearch('loise', fo, 0, props.config?.searchLimit ?? 20, makeGuardedSetData(gen));
    }, [props.deeplink]);

    const apiItems = [
        { label: 'Update', onClick: onClickUpdate },
        { label: 'Refresh', onClick: onClickRefresh },
        { label: 'Delete', onClick: onClickDelete },
    ];

    const editItems = [
        {
            label: 'Type',
            sub: [
                { label: 'Set Admin', onClick: onClickAdmin },
                { label: 'Set User', onClick: onClickUser },
            ],
        },
        {
            label: 'Status',
            sub: [
                { label: 'Set Block', onClick: onClickBlock },
                { label: 'Set Active', onClick: onClickActive },
                { label: 'Set Inactive', onClick: onClickInactive },
            ],
        },
        {
            label: 'Subscription',
            sub: [{ label: 'Set Subscription', onClick: onClickSubscription }],
        },
        {
            label: 'Badges',
            sub: [
                { label: 'Set Verified', onClick: onClickSetVerified },
                { label: 'Unset Verified', onClick: onClickUnsetVerified },
                { label: 'Set Membership', onClick: onClickSetMembership },
                { label: 'Unset Membership', onClick: onClickUnsetMembership },
            ],
        },
        {
            label: 'Legal',
            sub: ['privacy', 'terms', 'notice'].map((type) => ({
                label: type.charAt(0).toUpperCase() + type.slice(1),
                sub: [{ label: 'Unset', onClick: onClickUnsetLegal(type) }],
            })),
        },
        {
            label: 'Restriction',
            sub: [
                {
                    label: 'Post',
                    sub: [
                        { label: 'Set', onClick: onClickSetRestriction('post') },
                        { label: 'Unset', onClick: onClickUnsetRestriction('post') },
                    ],
                },
                {
                    label: 'Comment',
                    sub: [
                        { label: 'Set', onClick: onClickSetRestriction('comment') },
                        { label: 'Unset', onClick: onClickUnsetRestriction('comment') },
                    ],
                },
                {
                    label: 'Message',
                    sub: [
                        { label: 'Set', onClick: onClickSetRestriction('message') },
                        { label: 'Unset', onClick: onClickUnsetRestriction('message') },
                    ],
                },
            ],
        },
    ];

    return (
        <Stack direction='column' sx={{ p: 0, m: 0, width: '100%', height: '87vh', minHeight: 0 }}>
            <AdminMenuBar apiItems={apiItems} editItems={editItems} />

            {/* Filter header + collapse */}
            <Box
                sx={{
                    flexShrink: 1,
                    minHeight: 0,
                    maxHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                <Box sx={{ flexShrink: 0 }}>
                    <FilterDescHeader
                        filterDesc={filterDesc}
                        open={openFilter}
                        onToggle={() => setOpenFilter((f) => !f)}
                    />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <LoiseFilterCollapse
                        open={openFilter}
                        loading={loise.filter.loading}
                        filterData={loise.filter.data}
                        filterObject={filterObject}
                        onFilterChange={onFilterChange}
                        onSearch={onClickSearch}
                    />
                </Box>
                <Collapse in={openFilter} timeout='auto' unmountOnExit>
                    <Box sx={{ p: 1 }}>
                        <Button variant='contained' fullWidth onClick={onClickSearch}>
                            Search
                        </Button>
                    </Box>
                </Collapse>
            </Box>

            <Divider orientation='horizontal' variant='fullWidth' sx={{ flexShrink: 0 }} />

            {/* User list + info panel */}
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                {/* Left column: list */}
                <Box
                    sx={{
                        flex: '0 0 40%',
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: 1,
                        borderColor: 'primary.main',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            borderBottom: 1,
                            borderColor: 'primary.main',
                            boxSizing: 'border-box',
                        }}>
                        <ContainerScroll ref={scrollParentRef}>
                            <Virtuoso
                                ref={virtuosoRef}
                                style={{ height: '100%' }}
                                data={data.data}
                                overscan={200}
                                increaseViewportBy={{ top: 100, bottom: 100 }}
                                itemContent={(index) => {
                                    const user = data.data[index];
                                    return (
                                        <UserListItem
                                            {...props}
                                            key={user._id}
                                            user={user}
                                            type='search'
                                            selected={index === selectedIndex}
                                            onClick={() => setSelectedIndex(index)}
                                        />
                                    );
                                }}
                                components={{
                                    EmptyPlaceholder: () => <Message variant='m_body1' message='No users' />,
                                }}
                                customScrollParent={scrollParentRef.current}
                                endReached={() => {
                                    if (data.loadmore) loadMoreRows();
                                }}
                            />
                        </ContainerScroll>
                    </Box>
                </Box>

                {/* Right column: info */}
                <Box
                    sx={{
                        flex: '0 0 60%',
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            borderBottom: 1,
                            borderColor: 'primary.main',
                            boxSizing: 'border-box',
                        }}>
                        <LoiseUserInfoPanel
                            {...props}
                            user={getSelectedUser()}
                            pendingBadges={pendingBadges}
                            pendingRestriction={pendingRestriction}
                            pendingLegal={pendingLegal}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Dialogs */}
            {openType && (
                <LoiseUserTypeDialog
                    open={openType}
                    user={getSelectedUser()}
                    onClose={onCloseType}
                    onOK={onClickTypeOk}
                    onCancel={onCloseType}
                    targetType={typeValue}
                />
            )}
            {openStatus && (
                <LoiseUserStatusDialog
                    open={openStatus}
                    user={getSelectedUser()}
                    onClose={onCloseStatus}
                    onOK={onClickStatusOk}
                    onCancel={onCloseStatus}
                    targetStatus={statusValue}
                />
            )}
            {openDelete && (
                <LoiseUserDeleteDialog
                    open={openDelete}
                    user={getSelectedUser()}
                    onClose={onCloseDelete}
                    onOK={onClickDeleteOk}
                    onCancel={onCloseDelete}
                />
            )}
            {openSubscription && (
                <LoiseUserSubscriptionDialog
                    open={openSubscription}
                    user={getSelectedUser()}
                    onClose={onCloseSubscription}
                    onOK={onClickSubscriptionOk}
                    onCancel={onCloseSubscription}
                />
            )}
        </Stack>
    );
}

export { LoiseUsersSearch };
